import styled from "@emotion/styled";
import { useState, type MouseEvent } from "react";

import {
  DEFAULT_INPUT_HEIGHT,
  ellipsise,
  IconButton,
  PROP_PANEL_GUTTER,
  PROP_PANEL_PADDING,
  PropertiesPanelSection,
  SkiliticsIcon,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useCssBindings } from "./CssBindingsContext";

export const CssBindingsPropertyPanelSection = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const [tooltip, setTooltip] = useState<string | null>(null);

  const { bindings, onEditBinding, onAddBinding } = useCssBindings();

  const handleCopyClick = (bindingName: string) => (e: MouseEvent) => {
    e.stopPropagation();
    void navigator.clipboard.writeText(`var(${bindingName})`);
    setTooltip(formatMessage("copiedToClipboard"));
    setTimeout(() => setTooltip(null), 2000);
  };

  return (
    <StyledPropertiesPanelSection
      title={formatMessage("sectionTitle")}
      helperText={formatMessage("sectionHelperText")}
      controls={
        <IconButton icon="plus" data-test="add-binding-button" onClick={(e) => onAddBinding?.(e.currentTarget)} />
      }
      empty={!bindings.length}
    >
      {bindings.map((binding) => (
        <BindingWrapper key={binding.id} onClick={(e) => onEditBinding?.(binding.id, e.currentTarget)}>
          <SkiliticsIcon icon="advanced-settings" size={16} />
          <Label>{binding.config.name}</Label>
          <Tooltip title={tooltip ?? formatMessage("copyToClipboard")} placement="top-end">
            <IconButton
              icon="copy"
              data-test="copy-binding-button"
              onClick={handleCopyClick(binding.config.name as string)}
            />
          </Tooltip>
        </BindingWrapper>
      ))}
    </StyledPropertiesPanelSection>
  );
};

const formatStrings = {
  sectionTitle: {
    en: "Bindings",
  },
  sectionHelperText: {
    en: "Reference variables available in scope by creating CSS variables to use in your styles.",
  },
  addNamedValueLabel: {
    en: "Add",
  },
  copyToClipboard: {
    en: "Copy to clipboard",
  },
  copiedToClipboard: {
    en: "Copied!",
  },
  removeNamedValue: {
    en: "Remove binding",
  },
} satisfies FormatMessages;

const StyledPropertiesPanelSection = styled(PropertiesPanelSection)`
  border-bottom: solid 1px ${({ theme }) => theme.surfaceSoft};
  margin: 0 -${PROP_PANEL_PADDING - PROP_PANEL_GUTTER}px; // negative margin to counteract the padding.
`;

const Label = styled.div`
  border-bottom: ${({ theme }) => `1px solid ${theme.surfaceSoft}`};
  padding: 8px 0;
  height: ${DEFAULT_INPUT_HEIGHT}px;
  width: 100%;
  transition: all 0.2s ease-in-out;
  min-width: 0; // css hack to allow flex child to ellipsise.
  user-select: none;
  ${ellipsise};
`;

const BindingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 5px;
  width: 100%;
  cursor: pointer;
  overflow: hidden;

  &:hover,
  &:active,
  &.open {
    color: ${({ theme }) => theme.primary};
    ${Label} {
      background: ${({ theme }) => theme.surface};
      border-color: ${({ theme }) => theme.outline};
    }
  }
`;
